// frontend/src/App.js - CROW UI Revamp with AppShell navigation
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';

// Auth imports
import {
  AuthProvider,
  useAuth,
  LoginForm,
  RegisterForm,
  ProfileSettings,
  ProtectedRoute,
  PublicRoute,
  ForgotPasswordForm,
  ResetPasswordForm,
  EmailVerificationPage
} from './components/auth';
import { useSubscription } from './hooks/useSubscription';
import { VehicleProfilesGuard, JobManagementGuard } from './components/common/SubscriptionGuard';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { OutreachSuppression } from './components/admin/outreach/OutreachSuppression';
import { OutreachRuns } from './components/admin/outreach/OutreachRuns';
import { OutreachQueue } from './components/admin/outreach/OutreachQueue';
import JobManagement from './components/mechanics/JobManagement';
import BillingPage from './components/payments/BillingPage';

// Layout
import { ThemeProvider } from './context/ThemeContext';
import AppShell from './components/layout/AppShell';
import DashboardRouter from './components/dashboard/DashboardRouter';

// Fleet Management imports
import { FleetRoutes } from './components/fleet';

import TermsOfService from './components/legal/TermsOfService';
import EstimateApprovalPage from './components/public/EstimateApprovalPage';
import PrivacyPolicy from './components/legal/PrivacyPolicy';

// Vehicle Profile imports
import VehicleList from './components/VehicleList';

// AI Assistant
import VehicleAIPage from './components/ai/VehicleAIPage';

// Stripe imports (if configured)
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import SubscriptionSelection from './components/auth/SubscriptionSelection';

// Styles
import './App.css';

// React Query configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Component to check for new registrations and redirect
const NewRegistrationCheck = ({ children }) => {
  const { isNewRegistration, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && user) {
      const showSubscription = localStorage.getItem('show_subscription_selection');

      // Mechanics start a no-card 30-day trial at registration and go straight
      // into the app, so they must NOT be forced to the plan-selection/card step.
      // (TrialExpiryGate still routes them to plan selection once the trial lapses.)
      const isMechanic = user.user_type === 'mechanic';

      if ((isNewRegistration || showSubscription === 'true') &&
          !isMechanic &&
          location.pathname !== '/subscription-selection' &&
          location.pathname !== '/login' &&
          location.pathname !== '/register') {
        navigate('/subscription-selection');
      }
    }
  }, [isNewRegistration, user, loading, navigate, location]);

  return children;
};

// Stripe configuration (only if key is provided)
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// Dashboard is now in components/dashboard/DashboardRouter.js

// A mechanic counts as free-tier only when NEITHER source claims a paid tier.
//
// The two disagree in production, by design and by accident:
//   * they use different vocabularies — `users.subscription_tier` holds
//     'essential', which is not even permitted by the `subscriptions_tier_check`
//     constraint on `subscriptions.tier` (nearest value there: 'mechanic_essential');
//   * `subscription_service.get_user_subscription` lazily INSERTs a 'free' row
//     for any user that has none, ignoring `users.subscription_tier` entirely.
//
// So a paying mechanic reliably acquires a 'free' subscriptions row that masks
// their real tier. Reading `subscription.tier` first therefore locked paying
// mechanics out of the product: combined with a trial computed from account
// creation + 30 days, every mechanic returning after their first month was
// redirected to the paywall permanently.
//
// Where the two sources disagree, resolve toward access. Wrongly admitting a
// free user costs a little unpaid usage; wrongly excluding a paying one costs
// the customer.
export const isFreeTier = (subscription, user) => {
  const subTier = subscription?.tier;
  const userTier = user?.subscription_tier;
  const subSaysFree = !subTier || subTier === 'free';
  const userSaysFree = !userTier || userTier === 'free';
  return subSaysFree && userSaysFree;
};

// Trial Expiry Gate - redirects free-tier mechanics with expired trials to subscription page
const TrialExpiryGate = ({ children }) => {
  const { user, loading } = useAuth();
  const { subscription, subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  const allowedPaths = [
    '/subscription-selection',
    '/billing',
    '/profile',
    '/login',
    '/register',
    '/logout',
    '/terms',
    '/privacy',
    '/e/',
    '/verify-email',
    '/forgot-password',
    '/reset-password'
  ];

  useEffect(() => {
    if (loading || subscriptionLoading || !user) return;

    const isMechanic = user.user_type === 'mechanic';
    const isFreeTierNow = isFreeTier(subscription, user);
    const trialExpired = subscription?.is_expired === true;
    const isOnAllowedPath = allowedPaths.some(p => location.pathname.startsWith(p));

    if (isMechanic && isFreeTierNow && trialExpired && !isOnAllowedPath) {
      navigate('/subscription-selection', { replace: true });
    }
  }, [user, loading, subscription, subscriptionLoading, navigate, location]);

  if (loading || subscriptionLoading) return null;

  const isMechanic = user?.user_type === 'mechanic';
  const isFreeTierNow = isFreeTier(subscription, user);
  const trialExpired = subscription?.is_expired === true;
  const daysRemaining = subscription?.days_remaining;
  const isOnAllowedPath = allowedPaths.some(p => location.pathname.startsWith(p));

  if (isMechanic && isFreeTierNow && trialExpired && !isOnAllowedPath) {
    return null;
  }

  if (isMechanic && isFreeTierNow && !trialExpired && daysRemaining !== undefined && daysRemaining <= 7) {
    return (
      <>
        <div className="bg-warning-bg border-b border-amber-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-warning mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-small text-amber-800">
                Your free trial expires in <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong>.
                Subscribe now to keep access to all features.
              </span>
            </div>
            <button
              onClick={() => navigate('/subscription-selection')}
              className="ml-4 bg-warning text-white px-4 py-1.5 rounded-lg text-small font-medium hover:bg-amber-700 transition-colors flex-shrink-0 min-h-touch flex items-center"
            >
              Subscribe Now
            </button>
          </div>
        </div>
        {children}
      </>
    );
  }

  return children;
};

// Protected route with AppShell layout
const ProtectedPage = ({ children }) => (
  <ProtectedRoute>
    <ThemeProvider>
      <AppShell>
        {children}
      </AppShell>
    </ThemeProvider>
  </ProtectedRoute>
);

// Main App Component with all routes
function App() {
  const AppContent = () => (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <NewRegistrationCheck>
          <TrialExpiryGate>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginForm />
                  </PublicRoute>
                }
              />

              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <RegisterForm />
                  </PublicRoute>
                }
              />

              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPasswordForm />
                  </PublicRoute>
                }
              />

              <Route
                path="/reset-password"
                element={
                  <PublicRoute>
                    <ResetPasswordForm />
                  </PublicRoute>
                }
              />

              {/* Email Verification - Public */}
              <Route
                path="/verify-email"
                element={<EmailVerificationPage />}
              />

              <Route
                path="/subscription-selection"
                element={<SubscriptionSelection />}
              />

              {/* Legal Pages - Public */}
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/e/:token" element={<EstimateApprovalPage />} />

              {/* Protected Routes - All wrapped in AppShell */}
              <Route
                path="/"
                element={
                  <ProtectedPage>
                    <DashboardRouter />
                  </ProtectedPage>
                }
              />

              {/* Vehicle Management */}
              <Route
                path="/vehicle-profiles"
                element={
                  <ProtectedPage>
                    <VehicleProfilesGuard>
                      <VehicleList />
                    </VehicleProfilesGuard>
                  </ProtectedPage>
                }
              />

              {/* AI Assistant */}
              <Route
                path="/ai"
                element={
                  <ProtectedPage>
                    <VehicleAIPage />
                  </ProtectedPage>
                }
              />

              {/* Job Management */}
              <Route
                path="/jobs"
                element={
                  <ProtectedPage>
                    <JobManagementGuard>
                      {/* The job list, not the Overview tab: this is where
                          "create a job" lives, and what "Jobs" promises. */}
                      <JobManagement defaultTab="jobs" />
                    </JobManagementGuard>
                  </ProtectedPage>
                }
              />

              {/* Customers - renders JobManagement with customers tab */}
              <Route
                path="/customers"
                element={
                  <ProtectedPage>
                    <JobManagementGuard>
                      <JobManagement defaultTab="customers" />
                    </JobManagementGuard>
                  </ProtectedPage>
                }
              />
              <Route
                path="/inquiries"
                element={
                  <ProtectedPage>
                    <JobManagementGuard>
                      <JobManagement defaultTab="inquiries" />
                    </JobManagementGuard>
                  </ProtectedPage>
                }
              />

              {/* Invoices - renders JobManagement with invoices tab */}
              <Route
                path="/invoices"
                element={
                  <ProtectedPage>
                    <JobManagementGuard>
                      <JobManagement defaultTab="documents" />
                    </JobManagementGuard>
                  </ProtectedPage>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedPage>
                    <ProfileSettings />
                  </ProtectedPage>
                }
              />

              {/* Billing */}
              <Route
                path="/billing"
                element={
                  <ProtectedPage>
                    <BillingPage />
                  </ProtectedPage>
                }
              />

              {/* Admin */}
              <Route
                path="/admin"
                element={
                  <ProtectedPage>
                    <AdminDashboard />
                  </ProtectedPage>
                }
              />

              {/* Admin - Outreach suppression list */}
              <Route
                path="/admin/outreach/suppression"
                element={
                  <ProtectedPage>
                    <OutreachSuppression />
                  </ProtectedPage>
                }
              />

              {/* Admin - Outreach agent runs */}
              <Route
                path="/admin/outreach/runs"
                element={
                  <ProtectedPage>
                    <OutreachRuns />
                  </ProtectedPage>
                }
              />

              {/* Admin - Outreach review queue */}
              <Route
                path="/admin/outreach/queue"
                element={
                  <ProtectedPage>
                    <OutreachQueue />
                  </ProtectedPage>
                }
              />

              {/* Fleet Management */}
              <Route
                path="/fleet/*"
                element={
                  <ProtectedPage>
                    <FleetRoutes />
                  </ProtectedPage>
                }
              />

              {/* Catch all - redirect to dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          </TrialExpiryGate>
          </NewRegistrationCheck>
        </Router>

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1E293B',
              color: '#F1F5F9',
              borderRadius: '8px',
              fontSize: '0.9375rem',
            },
            success: {
              duration: 3000,
              style: {
                background: '#16A34A',
                color: '#fff',
              },
            },
            error: {
              duration: 5000,
              style: {
                background: '#DC2626',
                color: '#fff',
              },
            },
          }}
        />
      </AuthProvider>
    </QueryClientProvider>
  );

  // Wrap with Stripe Elements only if Stripe is configured
  if (stripePromise) {
    return (
      <Elements stripe={stripePromise}>
        <AppContent />
      </Elements>
    );
  }

  return <AppContent />;
}

export default App;
